import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    Layout, Card, Button, Spin, Alert, Modal, Divider, message, Tooltip, Tabs, Upload, Typography, Flex
} from 'antd';
import {
    UndoOutlined, DeleteOutlined, PlusOutlined, SendOutlined, UploadOutlined, LockOutlined
} from '@ant-design/icons';
import { Document, Page, pdfjs } from 'react-pdf';
import SignatureCanvas from 'react-signature-canvas';
import { Rnd } from 'react-rnd';
import * as publicActions from 'store/actions/PublicActions';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

const { Content, Header } = Layout;
const { Title, Text } = Typography;

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Signature Field Component
const SignatureField = ({ field, scaleFactor, onUpdate, onRemove, onClick, isActive, disabled }) => {
    const screenX = field.x * scaleFactor;
    const screenY = field.y * scaleFactor;
    const screenWidth = field.width * scaleFactor;
    const screenHeight = field.height * scaleFactor;
    const isLocked = field.isLocked;

    const getBorderStyle = () => {
        if (isLocked) return '2px solid #722ed1';
        if (isActive) return '2px solid #1890ff';
        if (field.signatureData) return '2px solid #52c41a';
        return '2px dashed #999';
    };

    return (
        <Rnd
            bounds="parent"
            size={{ width: screenWidth, height: screenHeight }}
            position={{ x: screenX, y: screenY }}
            disableDragging={disabled || isLocked}
            enableResizing={!disabled && !isLocked}
            onDragStop={(e, d) => {
                if (!disabled && !isLocked) {
                    onUpdate({ ...field, x: d.x / scaleFactor, y: d.y / scaleFactor });
                }
            }}
            onResizeStop={(e, direction, ref, delta, position) => {
                if (!disabled && !isLocked) {
                    onUpdate({
                        ...field,
                        x: position.x / scaleFactor,
                        y: position.y / scaleFactor,
                        width: ref.offsetWidth / scaleFactor,
                        height: ref.offsetHeight / scaleFactor
                    });
                }
            }}
            onClick={(e) => {
                e.stopPropagation();
                if (!isLocked) onClick();
            }}
            style={{
                border: getBorderStyle(),
                borderRadius: 4,
                background: field.signatureData ? 'rgba(255, 255, 255, 0.95)' : 'rgba(24, 144, 255, 0.1)',
                cursor: isLocked ? 'default' : (disabled ? 'default' : 'move'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                pointerEvents: 'auto'
            }}
        >
            {field.signatureData ? (
                <img
                    src={field.signatureData}
                    alt="Signature"
                    style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
                />
            ) : (
                <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', height: '100%', fontSize: 12,
                    color: '#666', textAlign: 'center', padding: 4
                }}>
                    <PlusOutlined style={{ fontSize: 16, marginBottom: 4 }} />
                    <span>Click to sign</span>
                </div>
            )}
            {isLocked && (
                <Tooltip title="Existing signature (locked)">
                    <div style={{
                        position: 'absolute', top: -8, right: -8, width: 20, height: 20,
                        borderRadius: '50%', background: '#722ed1', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, zIndex: 10
                    }}>
                        <LockOutlined />
                    </div>
                </Tooltip>
            )}
            {!disabled && !isLocked && (
                <Tooltip title="Remove signature">
                    <button
                        onClick={(e) => { e.stopPropagation(); onRemove(field.id); }}
                        style={{
                            position: 'absolute', top: -8, right: -8, width: 20, height: 20,
                            borderRadius: '50%', border: 'none', background: '#ff4d4f',
                            color: '#fff', cursor: 'pointer', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', fontSize: 10, zIndex: 10
                        }}
                    >
                        <DeleteOutlined />
                    </button>
                </Tooltip>
            )}
        </Rnd>
    );
};

const PublicSignerView = ({ document, recipient, fields, token, onComplete }) => {
    const sigCanvasRef = useRef(null);
    const pdfContainerRef = useRef(null);

    // State
    const [saving, setSaving] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [numPages, setNumPages] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(1.0);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [signerName, setSignerName] = useState(recipient?.name || '');
    const [hasDrawnSignature, setHasDrawnSignature] = useState(false);
    const [, setPdfLoading] = useState(true);
    const [pdfError, setPdfError] = useState(null);
    const [signatureMode, setSignatureMode] = useState('draw');
    const [uploadedSignaturePreview, setUploadedSignaturePreview] = useState(null);
    const [existingSignatures, setExistingSignatures] = useState([]);
    const [newSignatureFields, setNewSignatureFields] = useState([]);
    const [activeFieldId, setActiveFieldId] = useState(null);
    const [scaleFactor, setScaleFactor] = useState(1);

    // Combine signatures for display
    const allSignatureFields = useMemo(() => {
        return [...existingSignatures, ...newSignatureFields];
    }, [existingSignatures, newSignatureFields]);

    // Initialize PDF URL
    useEffect(() => {
        if (document?.document_file_url || document?.document_file) {
            setPdfUrl(document.document_file_url || document.document_file);
        }
        if (document?.signed_document_file) {
            setPdfUrl(document.signed_document_file);
        }
    }, [document]);

    // Load existing signatures/fields (both filled and unfilled pre-defined fields)
    useEffect(() => {
        if (fields && Array.isArray(fields)) {
            // Show ALL pre-defined fields - filled ones are locked, unfilled are clickable
            const existingSigs = fields.map((field, index) => ({
                id: `predefined_${field.id || index}`,
                x: field.position_x || 0,
                y: field.position_y || 0,
                width: field.width || 150,
                height: field.height || 50,
                page: field.page || 1,
                signatureData: field.signature_data || null,
                signerName: field.signer_name || '',
                isLocked: field.is_filled,  // Only lock if already filled
                isPredefined: true,
                fieldId: field.id  // Keep original field ID for submission
            }));
            setExistingSignatures(existingSigs);
        }
    }, [fields]);

    // Calculate scale factor - react-pdf renders at scale={zoomLevel},
    // so 1 PDF point = zoomLevel CSS pixels
    useEffect(() => {
        setScaleFactor(zoomLevel);
    }, [zoomLevel]);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setPdfLoading(false);
        setPdfError(null);
    };

    const onDocumentLoadError = (error) => {
        console.error("PDF load error:", error);
        setPdfLoading(false);
        setPdfError(error);
        message.error("Failed to load PDF document");
    };

    // Handle click on PDF to add signature field
    const handlePdfClick = (e, pageNumber) => {
        const pageElement = e.currentTarget;
        const rect = pageElement.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;
        const x = clickX / scaleFactor;
        const y = clickY / scaleFactor;

        const newField = {
            id: Date.now().toString(),
            x: Math.max(0, x - 75),
            y: Math.max(0, y - 25),
            width: 150,
            height: 50,
            page: pageNumber,
            signatureData: null,
            signerName: '',
            isLocked: false
        };

        setNewSignatureFields(prev => [...prev, newField]);
        setActiveFieldId(newField.id);
        setSignatureModalOpen(true);
    };

    const handleFieldUpdate = (updatedField) => {
        if (updatedField.isLocked) return;
        // Update in newSignatureFields or existingSignatures depending on where field is
        if (updatedField.isPredefined) {
            setExistingSignatures(prev =>
                prev.map(f => f.id === updatedField.id ? updatedField : f)
            );
        } else {
            setNewSignatureFields(prev =>
                prev.map(f => f.id === updatedField.id ? updatedField : f)
            );
        }
    };

    const handleFieldRemove = (fieldId) => {
        setNewSignatureFields(prev => prev.filter(f => f.id !== fieldId));
        if (activeFieldId === fieldId) setActiveFieldId(null);
    };

    const handleFieldClick = (field) => {
        if (field.isLocked) return;
        setActiveFieldId(field.id);
        if (!field.signatureData) setSignatureModalOpen(true);
    };

    const handleClearSignature = () => {
        if (sigCanvasRef.current) {
            sigCanvasRef.current.clear();
            setHasDrawnSignature(false);
        }
    };

    const handleSignatureImageUpload = (file) => {
        const isImage = file.type === 'image/png' || file.type === 'image/jpeg';
        if (!isImage) {
            message.error('Only PNG and JPG images are allowed');
            return false;
        }
        if (file.size / 1024 / 1024 >= 2) {
            message.error('Image must be smaller than 2MB');
            return false;
        }
        const reader = new FileReader();
        reader.onload = (e) => setUploadedSignaturePreview(e.target.result);
        reader.readAsDataURL(file);
        return false;
    };

    const resetModalState = () => {
        setSignatureMode('draw');
        setUploadedSignaturePreview(null);
        sigCanvasRef.current?.clear();
        setHasDrawnSignature(false);
    };

    const handleSaveSignatureToField = () => {
        let signatureBase64;
        if (signatureMode === 'draw') {
            if (sigCanvasRef.current.isEmpty()) {
                message.warning("Please draw a signature first");
                return;
            }
            signatureBase64 = sigCanvasRef.current.toDataURL('image/png');
        } else {
            if (!uploadedSignaturePreview) {
                message.warning("Please upload a signature image first");
                return;
            }
            signatureBase64 = uploadedSignaturePreview;
        }

        // Check if this is a pre-defined field or a new field
        const isPredefinedField = existingSignatures.some(f => f.id === activeFieldId);

        if (isPredefinedField) {
            setExistingSignatures(prev => prev.map(field =>
                field.id === activeFieldId
                    ? { ...field, signatureData: signatureBase64, signerName }
                    : field
            ));
        } else {
            setNewSignatureFields(prev => prev.map(field =>
                field.id === activeFieldId
                    ? { ...field, signatureData: signatureBase64, signerName }
                    : field
            ));
        }

        setSignatureModalOpen(false);
        sigCanvasRef.current?.clear();
        setHasDrawnSignature(false);
        setSignatureMode('draw');
        setUploadedSignaturePreview(null);
        message.success("Signature added");
    };

    // Submit signatures to backend
    const handleSubmitSignatures = async () => {
        // Get filled ad-hoc fields
        const filledNewFields = newSignatureFields.filter(f => f.signatureData);
        // Get newly filled pre-defined fields (not already filled on server)
        const filledPredefinedFields = existingSignatures.filter(f => f.signatureData && !f.isLocked);

        const allFilledFields = [...filledNewFields, ...filledPredefinedFields];

        if (allFilledFields.length === 0) {
            message.warning("Please add at least one signature by clicking on the document");
            return;
        }

        setSaving(true);
        try {
            const response = await publicActions.submitPublicSignature(token, {
                signatures: allFilledFields.map(field => ({
                    signature_image_as_base64: field.signatureData,
                    signer_name: field.signerName || signerName,
                    page: field.page,
                    position_x: field.x,
                    position_y: field.y,
                    width: field.width,
                    height: field.height
                }))
            });

            if (response?.success !== false) {
                onComplete?.();
            } else {
                message.error(response?.error || 'Failed to sign document');
            }
        } catch (error) {
            console.error('Signing error:', error);
            message.error('Failed to sign document. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // Count all signatures (both new and pre-defined that were newly filled)
    const filledNewSignaturesCount = newSignatureFields.filter(f => f.signatureData).length;
    const filledPredefinedCount = existingSignatures.filter(f => f.signatureData && !f.isLocked).length;
    const totalFilledCount = filledNewSignaturesCount + filledPredefinedCount;

    // Count unfilled pre-defined fields that still need signatures
    const unfilledPredefinedCount = existingSignatures.filter(f => !f.signatureData && !f.isLocked).length;
    const totalNewFieldsCount = newSignatureFields.length + unfilledPredefinedCount;

    return (
        <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
            {/* Simple Header */}
            <Header style={{
                background: '#fff',
                padding: '0 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
                <div>
                    <Title level={4} style={{ margin: 0 }}>
                        Sign Document
                    </Title>
                    <Text type="secondary">
                        {document?.title || document?.name}
                    </Text>
                </div>
                <div>
                    <Text style={{ marginRight: 16 }}>
                        Signing as: <strong>{recipient?.name}</strong>
                    </Text>
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSubmitSignatures}
                        disabled={totalFilledCount === 0}
                        loading={saving}
                    >
                        Submit Signature{totalFilledCount > 1 ? 's' : ''}
                    </Button>
                </div>
            </Header>

            <Content style={{ padding: 24 }}>
                <Alert
                    message="Click anywhere on the PDF to add your signature"
                    description="You can add multiple signatures, drag them to reposition, and resize them. After adding signatures, click Submit to complete."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />

                {/* PDF Preview */}
                <Card>
                    <div
                        ref={pdfContainerRef}
                        style={{
                            backgroundColor: '#666',
                            padding: 20,
                            borderRadius: 8,
                            maxHeight: 'calc(100vh - 300px)',
                            overflowY: 'auto'
                        }}
                    >
                        <Flex justify="center">
                            {pdfError ? (
                                <div style={{ color: '#fff', padding: 40, textAlign: 'center' }}>
                                    <p>Failed to load PDF</p>
                                </div>
                            ) : pdfUrl ? (
                                <Document
                                    file={pdfUrl}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                    onLoadError={onDocumentLoadError}
                                    loading={<Spin size="large" tip="Loading PDF..." />}
                                >
                                    {numPages > 0 && [...Array(numPages).keys()].map((_, index) => {
                                        const pageNumber = index + 1;
                                        const pageFields = allSignatureFields.filter(f => f.page === pageNumber);

                                        return (
                                            <div
                                                key={pageNumber}
                                                style={{ position: 'relative', marginBottom: 10, cursor: 'crosshair' }}
                                            >
                                                <div onClick={(e) => handlePdfClick(e, pageNumber)} style={{ position: 'relative' }}>
                                                    <Page
                                                        pageNumber={pageNumber}
                                                        scale={zoomLevel}
                                                        renderTextLayer={false}
                                                        renderAnnotationLayer={false}
                                                    />
                                                    <div style={{
                                                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                                        pointerEvents: 'none'
                                                    }}>
                                                        {pageFields.map(field => (
                                                            <SignatureField
                                                                key={field.id}
                                                                field={field}
                                                                scaleFactor={scaleFactor}
                                                                onUpdate={handleFieldUpdate}
                                                                onRemove={handleFieldRemove}
                                                                onClick={() => handleFieldClick(field)}
                                                                isActive={activeFieldId === field.id}
                                                                disabled={false}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                                <div style={{ textAlign: 'center', backgroundColor: '#fff', padding: '5px' }}>
                                                    Page {pageNumber} of {numPages}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Document>
                            ) : (
                                <div style={{ color: '#fff', padding: 40 }}>
                                    No PDF preview available
                                </div>
                            )}
                        </Flex>
                    </div>

                    {/* Zoom Controls */}
                    <Flex justify="center" style={{ marginTop: 16 }} gap={16} align="center">
                        <Button onClick={() => setZoomLevel(prev => Math.max(0.5, prev - 0.25))}>
                            Zoom Out
                        </Button>
                        <span>Zoom: {Math.round(zoomLevel * 100)}%</span>
                        <Button onClick={() => setZoomLevel(prev => Math.min(2, prev + 0.25))}>
                            Zoom In
                        </Button>
                    </Flex>

                    {/* Signature Summary */}
                    {(totalNewFieldsCount > 0 || totalFilledCount > 0) && (
                        <Flex justify="center" style={{ marginTop: 10 }}>
                            <Alert
                                message={`${totalFilledCount} signature(s) ready to submit`}
                                type={totalFilledCount > 0 ? "success" : "info"}
                                showIcon
                            />
                        </Flex>
                    )}
                </Card>
            </Content>

            {/* Signature Modal */}
            <Modal
                title="Add Your Signature"
                open={signatureModalOpen}
                onCancel={() => {
                    resetModalState();
                    setSignatureModalOpen(false);
                    const activeField = newSignatureFields.find(f => f.id === activeFieldId);
                    if (activeField && !activeField.signatureData) {
                        handleFieldRemove(activeFieldId);
                    }
                }}
                width={600}
                footer={[
                    <Button
                        key="clear"
                        icon={<UndoOutlined />}
                        onClick={signatureMode === 'draw' ? handleClearSignature : () => setUploadedSignaturePreview(null)}
                    >
                        Clear
                    </Button>,
                    <Button key="cancel" onClick={() => {
                        resetModalState();
                        setSignatureModalOpen(false);
                        const activeField = newSignatureFields.find(f => f.id === activeFieldId);
                        if (activeField && !activeField.signatureData) {
                            handleFieldRemove(activeFieldId);
                        }
                    }}>
                        Cancel
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={handleSaveSignatureToField}
                        disabled={signatureMode === 'draw' ? !hasDrawnSignature : !uploadedSignaturePreview}
                    >
                        Add Signature
                    </Button>
                ]}
            >
                <div style={{ marginBottom: 16 }}>
                    <label>Your Name:</label>
                    <input
                        type="text"
                        value={signerName}
                        onChange={(e) => setSignerName(e.target.value)}
                        placeholder="Enter your name"
                        style={{
                            width: '100%', padding: '8px', borderRadius: '4px',
                            border: '1px solid #d9d9d9', marginTop: '4px'
                        }}
                    />
                </div>

                <Tabs
                    activeKey={signatureMode}
                    onChange={(key) => setSignatureMode(key)}
                    items={[
                        {
                            key: 'draw',
                            label: 'Draw Signature',
                            children: (
                                <>
                                    <p>Draw your signature in the box below:</p>
                                    <div style={{
                                        border: hasDrawnSignature ? '2px solid #52c41a' : '2px solid #d9d9d9',
                                        borderRadius: 8, backgroundColor: '#fff'
                                    }}>
                                        <SignatureCanvas
                                            ref={sigCanvasRef}
                                            onEnd={() => setHasDrawnSignature(true)}
                                            canvasProps={{
                                                width: 550, height: 200,
                                                className: 'signature-canvas',
                                                style: { borderRadius: '6px' }
                                            }}
                                            penColor="black"
                                        />
                                    </div>
                                </>
                            )
                        },
                        {
                            key: 'upload',
                            label: 'Upload Image',
                            children: (
                                <>
                                    <p>Upload a signature image (PNG or JPG, max 2MB):</p>
                                    <Upload
                                        accept=".png,.jpg,.jpeg"
                                        beforeUpload={handleSignatureImageUpload}
                                        showUploadList={false}
                                        maxCount={1}
                                    >
                                        <Button icon={<UploadOutlined />}>
                                            {uploadedSignaturePreview ? 'Change Image' : 'Select Image'}
                                        </Button>
                                    </Upload>
                                    {uploadedSignaturePreview && (
                                        <div style={{
                                            marginTop: 16, border: '2px solid #52c41a',
                                            borderRadius: 8, padding: 10, backgroundColor: '#fff'
                                        }}>
                                            <img
                                                src={uploadedSignaturePreview}
                                                alt="Signature preview"
                                                style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                                            />
                                        </div>
                                    )}
                                </>
                            )
                        }
                    ]}
                />

                <Divider />
                <p style={{ color: '#666', fontSize: 12 }}>
                    By signing this document, you agree to the terms and conditions contained within.
                </p>
            </Modal>
        </Layout>
    );
};

export default PublicSignerView;
